<div
  class="landing"
  [attr.lang]="lang()"
  [attr.data-font]="fontScale()"
  [attr.data-contrast]="highContrast() ? 'on' : 'off'"
>
  <div class="tricolor" aria-hidden="true"></div>

  <div class="a11y-bar">
    <div class="a11y-inner">
      <div class="a11y-left">
        <a href="#main">{{ t().skip }}</a>
        <a href="#main">{{ t().screenReader }}</a>
      </div>
      <div class="a11y-right">
        <button type="button" [class.active]="fontScale() === 'sm'" (click)="setFont('sm', $event)" aria-label="Decrease font">A−</button>
        <button type="button" [class.active]="fontScale() === 'md'" (click)="setFont('md', $event)" aria-label="Default font">A</button>
        <button type="button" [class.active]="fontScale() === 'lg'" (click)="setFont('lg', $event)" aria-label="Increase font">A+</button>
        <button type="button" class="contrast" [class.active]="highContrast()" (click)="toggleContrast($event)">{{ t().highContrast }}</button>
      </div>
    </div>
  </div>

  <header class="topbar">
    <div class="topbar-inner">
      <a routerLink="/" class="brand" aria-label="Jansunwai home">
        <img
          class="seal up"
          src="/deoria-government-logo.jpg"
          alt="Government of Uttar Pradesh"
        />
        <span class="brand-text">
          <span class="gov">{{ t().gov }}</span>
          <span class="org-en">{{ t().org }}</span>
        </span>
      </a>

      <nav class="page-nav" [class.open]="menuOpen()" aria-label="Page">
        <a href="#about" (click)="scrollTo('about', $event)">{{ t().navHow }}</a>
        <a href="#services" (click)="scrollTo('services', $event)">{{ t().navServices }}</a>
        <a href="#faq" (click)="scrollTo('faq', $event)">{{ t().navFaq }}</a>
      </nav>

      <div class="top-actions">
        <button
          type="button"
          class="menu-btn"
          (click)="toggleMenu()"
          [attr.aria-expanded]="menuOpen()"
          aria-label="Toggle menu"
        >
          <svg viewBox="0 0 24 24" width="20" height="20" fill="none" stroke="currentColor" stroke-width="2" aria-hidden="true">
            @if (menuOpen()) {
              <path d="M6 6l12 12M18 6L6 18" />
            } @else {
              <path d="M4 7h16M4 12h16M4 17h16" />
            }
          </svg>
        </button>

        <div class="lang" role="group" [attr.aria-label]="lang() === 'hi' ? 'भाषा' : 'Language'">
          <button
            type="button"
            class="chip"
            [class.active]="lang() === 'en'"
            (click)="setLang('en', $event)"
            aria-label="English"
          >
            EN
          </button>
          <button
            type="button"
            class="chip"
            [class.active]="lang() === 'hi'"
            (click)="setLang('hi', $event)"
            aria-label="हिन्दी"
          >
            अ
          </button>
        </div>

        <a [routerLink]="entryLink()" class="login-btn">{{ entryLabel() }}</a>
      </div>
    </div>
  </header>

  <section class="hero" id="main" [attr.aria-label]="t().brandTitle">
    <div class="hero-media" aria-hidden="true">
      <img
        src="https://images.unsplash.com/photo-1760872645824-49f21b002e61?auto=format&fit=crop&w=2400&q=80"
        alt=""
      />
      <div class="veil"></div>
    </div>

    <div class="hero-copy">
      <p class="brand-mark">{{ t().brandMark }}</p>
      <h1>{{ t().brandTitle }}</h1>
      <p class="support">{{ t().support }}</p>
      <div class="cta-row">
        <a [routerLink]="entryLink()" class="cta solid">{{ entryLabel() }}</a>
      </div>
    </div>

    <button type="button" class="scroll-cue" (click)="scrollTo('about', $event)">
      <span>{{ t().scrollHint }}</span>
      <span class="chev" aria-hidden="true"></span>
    </button>
  </section>

  <section class="about" id="about">
    <div class="about-inner reveal">
      <p class="eyebrow">{{ t().howEyebrow }}</p>
      <h2>{{ t().howTitle }}</h2>
      <p class="section-lead">{{ t().howLead }}</p>
      <ol class="steps">
        @for (step of [
          { title: t().step1Title, body: t().step1Body },
          { title: t().step2Title, body: t().step2Body },
          { title: t().step3Title, body: t().step3Body },
          { title: t().step4Title, body: t().step4Body }
        ]; track step.title; let i = $index) {
          <li [class.active]="activeStep() === i" (mouseenter)="selectStep(i)" (focusin)="selectStep(i)">
            <button type="button" class="step-hit" (click)="selectStep(i)">
              <span class="step-num">{{ ['01', '02', '03', '04'][i] }}</span>
              <span class="step-text">
                <span class="step-title">{{ step.title }}</span>
                <span class="step-body">{{ step.body }}</span>
              </span>
            </button>
          </li>
        }
      </ol>
    </div>
  </section>

  <section class="services" id="services">
    <div class="services-inner reveal">
      <div class="services-copy">
        <p class="eyebrow">{{ t().servicesEyebrow }}</p>
        <h2>{{ t().servicesTitle }}</h2>
        <p class="section-lead">{{ t().servicesLead }}</p>

        <div class="tabs" role="tablist">
          <button
            type="button"
            role="tab"
            [attr.aria-selected]="serviceTab() === 'citizen'"
            [class.active]="serviceTab() === 'citizen'"
            (click)="setServiceTab('citizen')"
          >
            {{ t().tabCitizen }}
          </button>
          <button
            type="button"
            role="tab"
            [attr.aria-selected]="serviceTab() === 'employee'"
            [class.active]="serviceTab() === 'employee'"
            (click)="setServiceTab('employee')"
          >
            {{ t().tabEmployee }}
          </button>
        </div>

        <div class="tab-panel" role="tabpanel">
          <ul class="bullet-list">
            @for (point of serviceTab() === 'citizen' ? t().citizenPoints : t().employeePoints; track point) {
              <li>{{ point }}</li>
            }
          </ul>
          <div class="panel-cta">
            @if (serviceTab() === 'employee') {
              <a [routerLink]="entryLink()" class="cta solid ink">{{ entryLabel() }}</a>
            }
          </div>
        </div>
      </div>

      <aside class="demo-box" [attr.aria-label]="t().demoTitle">
        <p class="demo-point">
          <span class="demo-point-kicker">{{ t().demoKicker }}</span>
          {{ t().demoPoint }}
          <span class="demo-arrow" aria-hidden="true"></span>
        </p>
        <div class="demo-frame" [class.playing]="demoPlaying()">
          <span class="demo-trim" aria-hidden="true"></span>
          <video
            #demoVideo
            class="demo-video"
            playsinline
            preload="metadata"
            [controls]="demoPlaying()"
            [attr.aria-label]="t().demoTitle"
            (play)="onDemoPlay()"
            (pause)="onDemoPause()"
            (ended)="onDemoEnded()"
          >
            <source src="/jansunwai-walkthrough.mp4" type="video/mp4" />
          </video>
          @if (!demoPlaying()) {
            <button type="button" class="demo-play" (click)="playDemo()" [attr.aria-label]="t().demoPlay">
              <span class="demo-ring" aria-hidden="true"></span>
              <span class="demo-ring delay" aria-hidden="true"></span>
              <span class="demo-icon" aria-hidden="true">
                <svg viewBox="0 0 24 24" width="26" height="26" focusable="false">
                  <path fill="currentColor" d="M8.2 5.2v13.6L19.2 12 8.2 5.2z" />
                </svg>
              </span>
            </button>
          }
          <p class="demo-caption">{{ t().demoTitle }}</p>
        </div>
      </aside>
    </div>
  </section>

  <section class="status-guide" id="status">
    <div class="status-inner reveal">
      <div class="status-copy">
        <p class="eyebrow">{{ t().statusEyebrow }}</p>
        <h2>{{ t().statusTitle }}</h2>
        <p class="section-lead">{{ t().statusLead }}</p>
      </div>
      <div class="status-board">
        <div class="status-rail" role="list">
          @for (s of t().statuses; track s.code; let i = $index) {
            <button
              type="button"
              role="listitem"
              class="status-chip"
              [class.active]="activeStatus() === i"
              (click)="selectStatus(i)"
            >
              {{ s.label }}
            </button>
          }
        </div>
        <p class="status-hint">{{ t().statuses[activeStatus()].hint }}</p>
      </div>
    </div>
  </section>

  <section class="need" id="need">
    <div class="need-inner reveal">
      <div>
        <p class="eyebrow">{{ t().needEyebrow }}</p>
        <h2>{{ t().needTitle }}</h2>
        <p class="section-lead">{{ t().needLead }}</p>
      </div>
      <ol class="need-list">
        @for (item of t().needItems; track item; let i = $index) {
          <li>
            <span class="need-num">{{ i + 1 }}</span>
            <span>{{ item }}</span>
          </li>
        }
      </ol>
    </div>
  </section>

  <section class="faq" id="faq">
    <div class="faq-inner reveal">
      <p class="eyebrow">{{ t().faqEyebrow }}</p>
      <h2>{{ t().faqTitle }}</h2>
      <div class="faq-list">
        @for (item of t().faqs; track item.q; let i = $index) {
          <div class="faq-item" [class.open]="openFaq() === i">
            <button type="button" class="faq-q" [attr.aria-expanded]="openFaq() === i" (click)="toggleFaq(i)">
              <span>{{ item.q }}</span>
              <span class="faq-icon" aria-hidden="true"></span>
            </button>
            @if (openFaq() === i) {
              <div class="faq-a">
                <p>{{ item.a }}</p>
              </div>
            }
          </div>
        }
      </div>
    </div>
  </section>

  <section class="help" id="help">
    <div class="help-inner reveal">
      <div class="help-copy">
        <h2>{{ t().helpTitle }}</h2>
        <p>{{ t().helpBody }}</p>
      </div>
      <dl class="help-meta">
        <div>
          <dt>{{ t().helpDesk }}</dt>
          <dd>{{ t().helpHours }}</dd>
        </div>
        <div>
          <dt>Email</dt>
          <dd><a [href]="'mailto:' + t().helpMail">{{ t().helpMail }}</a></dd>
        </div>
      </dl>
    </div>
  </section>

  <footer class="foot">
    <div class="foot-inner">
      <div>
        <p>{{ t().footMain }}</p>
        <p class="foot-links">{{ t().footLinks }}</p>
      </div>
      <p>{{ t().footAuth }}</p>
    </div>
  </footer>
</div>
